import React, { FC, useMemo, useState } from 'react';
import { PlayInactiveIcon } from '@getgo/chameleon-icons/react';
import { Button } from '@getgo/chameleon-web/react';
import { ConferenceStatusUtils } from '../../utils/conferenceStatusUtils';
import { ButtonProps } from '@getgo/chameleon-web/react/src/Button/Button.shell';
import AddEventButton, { AddEventButtonTheme } from '../../addEvent/AddEventButton';
import { IAgendaConference } from '../../event/eventInterface';
import { useTranslation } from 'react-i18next';
import {
  ON_JOIN_SESSION_CLICK,
  ON_JOIN_SESSION,
  ON_WATCH_RECORDING_CLICK,
  ON_WATCH_RECORDING,
} from '../../../mixpanel/mixpanelConstants';
import { eventDayDifference } from '../../utils/eventDataFormat';

export interface ConferenceStatusProps {
  status: string;
  joinWebinar: (
    conferenceKey: string,
    actionType: 'JOIN_LINK' | 'VIEW_RECORDING',
    eventName: string,
    eventNameStatus: string,
  ) => Promise<void>;
  conference: IAgendaConference;
  hasJoinedSession?: boolean;
  eventKey?: string;
}

const ConferenceStatus: FC<ConferenceStatusProps> = ({
  status,
  joinWebinar,
  conference,
  hasJoinedSession = false,
  eventKey,
}) => {
  const [initialStatus] = useState(status);
  const { t } = useTranslation();
  const isSessionFinished = status.toLowerCase() === ConferenceStatusUtils.status.finished;
  const defaultStatus = t('conference.remindMe');
  const defaultButtonStyle: ButtonProps = { variant: 'tertiary', size: 'small' };

  const eventEndDate = useMemo<Date>(() => new Date(conference.end), [conference.end]);
  const eventStartDate = useMemo<Date>(() => new Date(conference.start), [conference.start]);

  const [reminderStatus] = useState(defaultStatus);

  const conferenceAnimation = useMemo(() => {
    if (status !== initialStatus) return 'fade-in-button';
    return '';
  }, [status, initialStatus]);

  return (
    <div
      className={`conference-status ${conferenceAnimation} ${
        conference?.speakers.length ? 'conferenceStatusMargin' : ''
      }`}
    >
      {(status === '' || status.toLowerCase() === ConferenceStatusUtils.status.startingSoon) && (
        <AddEventButton
          conferenceKey={conference.conferenceKey}
          buttonName={defaultStatus}
          eventEndDate={eventEndDate}
          eventStartDate={eventStartDate}
          title={conference.title}
          theme={AddEventButtonTheme.tertiarySmall}
          timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
          isConferenceButton={reminderStatus !== t('conference.reminderSent')}
          eventKey={eventKey}
        />
      )}
      {isSessionFinished && conference.recordingAvailable && (
        <Button
          {...defaultButtonStyle}
          trailingIcon={<PlayInactiveIcon />}
          onClick={() =>
            joinWebinar(
              conference.conferenceKey,
              'VIEW_RECORDING',
              ON_WATCH_RECORDING_CLICK,
              ON_WATCH_RECORDING,
            )
          }
        >
          {t('conference.watchRecording')}
        </Button>
      )}
      {isSessionFinished &&
        !conference.recordingAvailable &&
        conference.recordingInitiated &&
        eventDayDifference(conference.end).days > -1 && (
          <Button {...defaultButtonStyle} trailingIcon={<PlayInactiveIcon />} disabled>
            {t('conference.recordingIsProcessing')}
          </Button>
        )}
      {status.toLowerCase() === ConferenceStatusUtils.status.ongoing && !hasJoinedSession && (
        <Button
          {...{ ...defaultButtonStyle, variant: 'primary' }}
          onClick={() =>
            joinWebinar(
              conference.conferenceKey,
              'JOIN_LINK',
              ON_JOIN_SESSION_CLICK,
              ON_JOIN_SESSION,
            )
          }
        >
          {t('conference.joinSession')}
        </Button>
      )}
      {status.toLowerCase() === ConferenceStatusUtils.status.ongoing && hasJoinedSession && (
        <Button {...defaultButtonStyle} disabled>
          {t('conference.joinedSession')}
        </Button>
      )}
    </div>
  );
};

export default ConferenceStatus;
